import React from 'react';

import APIDocsCurl from 'common/developers/docs/api/APIDocsCurl';
import APIDocsMethod from 'common/developers/docs/api/APIDocsMethod';
import Highlight from 'common/developers/docs/api/Highlight';

import APIDocsBlock from '../APIDocsBlock';
import APIDocsTable from '../APIDocsTable';

const APIDocsAIEnqueueFeedbackBlock = () => {
  return (
    <APIDocsBlock>
      <div>
        <div className="heading">Enqueue feedback</div>
        <div className="text">
          Adds an item of feedback to the queue. During the next sync, Autopilot will extract any
          feature requests and identify any duplicates. You can see the results when they are done
          processing.
          <br />
          <br />
          When in manual mode, the Autopilot inbox has a limit of 100 items. If you exceed this
          limit, you won't be able to enqueue any more items until you process them. Enabling
          automated mode is recommended to skip this limit, and view an audit log of the actions
          taken by Autopilot.
        </div>
        <APIDocsTable
          title="Arguments"
          rows={[
            {
              description: 'Your secret API key.',
              heading: 'apiKey',
              subheading: 'string',
            },
            {
              description:
                'The payload of the request detailing the author, sourceURL and source text. See below for more details.',
              heading: 'payload',
              subheading: 'object',
            },
            {
              description: 'The unique identifier of the conversation or chat you are enqueuing.',
              heading: 'payload.id',
              subheading: 'string',
            },
            {
              description: (
                <>
                  The author's unique identifier in your application. If the user does not exist,
                  you can create them with the{' '}
                  <a href="#create_or_update_user">"Create or Update User" endpoint</a> first.
                </>
              ),
              heading: 'payload.authorID',
              subheading: 'string',
            },
            {
              description: 'The text you want to extract features from e.g. a call transcript.',
              heading: 'payload.text',
              subheading: 'string',
            },
            {
              description: 'An optional URL linking to the conversation or chat you are enqueuing.',
              heading: 'payload.sourceURL',
              subheading: 'string (optional)',
            },
            {
              description:
                'An optional indicator of the source e.g. X (Twitter), Zoom, Instagram, ...',
              heading: 'payload.sourceType',
              subheading: 'string (optional)',
            },
            {
              description:
                'The type of content being enqueued. Currently only supports "call" and "conversation". Choose "call" if the transcript is from a call and "conversation" if it is from a chat conversation.',
              heading: 'type',
              subheading: 'string',
            },
          ]}
        />
        <div className="subheading">Returns</div>
        <div className="text">
          Returns "success" if the payload was enqueued. Otherwise an error describing the issue
          with your request.
        </div>
      </div>
      <div>
        <div className="text">Endpoint</div>
        <Highlight lang="http">https://canny.io/api/v1/ai/enqueue</Highlight>
        <APIDocsMethod />
        <APIDocsCurl
          path="https://canny.io/api/v1/ai/enqueue"
          body={`-d '{
    "apiKey": "YOUR_API_KEY",
    "payload": {
      "id": "conversationID",
      "authorID": "5bc799625ca6bf6caff284dd",
      "sourceURL": "https://twitter.com/cannyHQ/status/1728150249263218855",
      "sourceType": "twitter",
      "text": "I love this product! No feature requests, just wanted to say thanks!"
    },
    "type": "conversation"
    }' \\
    -H "Content-Type: application/json"`}
        />
        <div className="text">Example Response</div>
        <Highlight lang="json">{`"success"`}</Highlight>
      </div>
    </APIDocsBlock>
  );
};

export default APIDocsAIEnqueueFeedbackBlock;
